import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Map, Users, AlertCircle, Mail, Lock, CheckCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { useAuthStore, AUTH_ERROR_CODES } from "../stores/authStore";
import { handlePortaleHandshake, handleHandshakeSessionToken } from "../lib/auth";
import { supabase } from "../lib/supabase";
import packageJson from "../../package.json";
import Logo from "../assets/logo.svg";
import LogoIcon from "../assets/logoicon.svg";

// Dev-only test credentials
const DEV_EMAIL = import.meta.env.DEV ? "imprese_collaudo@yopmail.com" : "";
const DEV_PASSWORD = import.meta.env.DEV ? "#e6eNh2Xwpv@cvCYU2$v" : "";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, error, errorCode, setError } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState(DEV_EMAIL);
  const [password, setPassword] = useState(DEV_PASSWORD);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate("/map", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Clear session expiry errors when component mounts
  // This prevents showing "sessione scaduta" message when just loading the login page
  // The error will be shown again if login fails or if there's a new session expiry
  useEffect(() => {
    if (error && (errorCode === AUTH_ERROR_CODES.TOKEN_EXPIRED || errorCode === AUTH_ERROR_CODES.SESSION_TIMEOUT)) {
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Process handshake session token (from Portale Operatori redirect)
  const processHandshakeToken = useCallback(async (sessionToken: string, userId: string, jwt?: string) => {
    setIsProcessing(true);
    try {
      const result = await handleHandshakeSessionToken(sessionToken, userId);
      if (result.success && result.user) {
        // Store session with JWT (for API calls) if available, otherwise use session token
        // The JWT is needed for calling operators-query and other edge functions
        const tokenToStore = jwt || sessionToken;
        useAuthStore.getState().setUser(result.user, tokenToStore);
        navigate("/map", { replace: true });
      } else {
        setError(result.error || "Token non valido o sessione scaduta");
      }
    } catch (err) {
      console.error("Handshake token error:", err);
      setError("Errore durante l'autenticazione");
    } finally {
      setIsProcessing(false);
    }
  }, [navigate, setError]);

  // Process JWT token (for direct login, not from handshake)
  const processJwtToken = useCallback(async (token: string) => {
    setIsProcessing(true);
    try {
      const result = await handlePortaleHandshake(token);
      if (result.success && result.user) {
        useAuthStore.getState().setUser(result.user, token);
        navigate("/map", { replace: true });
      } else {
        setError(result.error || "Token non valido o sessione scaduta");
      }
    } catch (err) {
      console.error("JWT validation error:", err);
      setError("Errore durante la validazione del token");
    } finally {
      setIsProcessing(false);
    }
  }, [navigate, setError]);

  // Check for handshake session token in URL params (from Portale Operatori redirect)
  useEffect(() => {
    const token = searchParams.get("token");
    const userId = searchParams.get("user");
    const jwt = searchParams.get("jwt");

    if (token && userId && jwt) {
      // This is a handshake redirect with both session token and JWT
      // Decode JWT from base64url
      try {
        const decodedJWT = atob(jwt.replace(/-/g, "+").replace(/_/g, "/"));
        setIsProcessing(true);
        processHandshakeToken(token, userId, decodedJWT);
      } catch (e) {
        console.error("Error decoding JWT:", e);
        setError("Errore durante la decodifica del token");
      }
    } else if (token && userId) {
      // Fallback: handshake without JWT (shouldn't happen in normal flow)
      setIsProcessing(true);
      processHandshakeToken(token, userId);
    } else if (token) {
      // This might be a JWT token (for direct login)
      setIsProcessing(true);
      processJwtToken(token);
    }
  }, [searchParams, processHandshakeToken, processJwtToken, setError]);

  // Login with Portale Operatori credentials via Edge Function
  const handleCredentialsLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    setLoginSuccess(false);

    try {
      // Call Supabase Edge Function as proxy
      const { data, error: fnError } = await supabase.functions.invoke("auth-proxy", {
        body: { email, password, environment: "collaudo" },
      });

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (data?.success && data?.user) {
        setLoginSuccess(true);

        // Set user in store with token expiry
        // IMPORTANT: data.user.id is now the Supabase UUID (from auth-proxy sync)
        // data.user.portale_id is the original Portale Operatori ID
        useAuthStore.getState().setUser(
          {
            id: data.user.id, // Supabase UUID for database operations
            portale_id: data.user.portale_id,
            ext_id: data.user.ext_id,
            email: data.user.email,
            first_name: data.user.name || "",
            last_name: data.user.surname || "",
            tax_id: data.user.cfPiva || "",
            role: data.user.role || "LR",
            status: data.user.status || "ACTIVE",
            created_at: new Date().toISOString(),
          },
          data.token,
          data.expiresIn // Pass token expiry (3600 seconds from Cognito)
        );

        // Small delay to show success message
        setTimeout(() => {
          navigate("/map", { replace: true });
        }, 500);
        return;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      throw new Error("Risposta non valida dal server");
    } catch (err) {
      console.error("Login error:", err);

      // Check if it's a Supabase connection error
      if (err instanceof Error && (
        err.message.includes("Failed to fetch") ||
        err.message.includes("FunctionsHttpError") ||
        err.message.includes("FunctionsFetchError")
      )) {
        setError(
          "Edge Functions non disponibili. Assicurati che Supabase sia in esecuzione con: supabase functions serve"
        );
      } else {
        setError(err instanceof Error ? err.message : "Errore durante l'autenticazione");
      }
    } finally {
      if (!loginSuccess) {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Background Image - Italian Tourism Theme */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-navy-900"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1534445867742-43195f401b6c?q=80&w=2070&auto=format&fit=crop')`,
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-lg"
      >
        {/* Login Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-2">
                <img
                  src={Logo}
                  alt="DPM logo"
                  className="h-12 w-auto hidden sm:block"
                  loading="lazy"
                />
                <img
                  src={LogoIcon}
                  alt="DPM logo"
                  className="h-12 w-auto sm:hidden"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Success Message */}
            {loginSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-lg bg-green-500 text-white border border-green-600 flex items-center gap-3"
              >
                <CheckCircle className="h-5 w-5" />
                <p className="text-sm">Login riuscito! Reindirizzamento...</p>
              </motion.div>
            )}

            {error && !loginSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${errorCode === AUTH_ERROR_CODES.TOKEN_EXPIRED || errorCode === AUTH_ERROR_CODES.SESSION_TIMEOUT
                  ? "bg-amber-500/90 text-white border-amber-600"
                  : "bg-red-500 text-white border-red-600"
                  }`}
              >
                {errorCode === AUTH_ERROR_CODES.TOKEN_EXPIRED || errorCode === AUTH_ERROR_CODES.SESSION_TIMEOUT ? (
                  <Clock className="h-5 w-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="text-sm font-medium">{error}</p>
                  {(errorCode === AUTH_ERROR_CODES.TOKEN_EXPIRED || errorCode === AUTH_ERROR_CODES.SESSION_TIMEOUT) && (
                    <p className="text-xs mt-1 opacity-90">
                      Per motivi di sicurezza, effettua nuovamente l'accesso.
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Login Form */}
            <form onSubmit={handleCredentialsLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email
                </label>
                <Input
                  type="email"
                  placeholder="email@esempio.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isProcessing}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  Password
                </label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isProcessing}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isProcessing || !email || !password}
                isLoading={isProcessing}
              >
                <Shield className="h-4 w-4 mr-2" />
                Accedi con Portale Operatori
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 grid grid-cols-3 gap-4"
        >
          {[
            { icon: Map, label: "Mappa interattiva" },
            { icon: Users, label: "84k+ operatori" },
            { icon: Shield, label: "Accesso sicuro" },
          ].map((feature) => (
            <div
              key={feature.label}
              className="flex flex-col items-center gap-2 p-4 rounded-lg overlay-solid border border-border"
            >
              <feature.icon className="h-6 w-6 text-amber-400" />
              <span className="text-xs text-muted-foreground text-center">
                {feature.label}
              </span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Version */}
      <div className="absolute bottom-4 right-4 text-xs text-white/50">
        DPM v{packageJson.version}
      </div>
    </div>
  );
}
